import React, {useMemo} from 'react';
import TestPanelSection from './TestPanelSection';
import BiomarkerChartCard from './BiomarkerChartCard';
import {getTestEducation} from '@/shared/data/testEducation';
import {useAppSelector} from '@/shared/hooks/redux';
import {useTestsByName} from '@/modules/patient-dashboard/hooks/useTestsQuery';
import {selectCurrentPatient} from '@/slices/patientSlice';
import {
    determineStatus,
    parseRefRangeWithDefault,
    formatTestName,
    parseNumericValue,
} from '@/shared/utils/biomarkerUtils';
import {severityTier} from '@/shared/biomarkers/classify';
import {getFilterDisplayName, getHistoricalData} from '@/shared/utils/resultUtils';
import {HEALTH_CATEGORIES, getTestCategory} from '@/modules/patient-dashboard/utils/healthCategories';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {useDisplayRules} from '@/shared/hooks/useDisplayRules';

interface Props {
    result: any;
    test_type?: string;
    sortBy?: string;
    filterBy?: string;
    recommendations?: any[];
    onClearFilters?: () => void;
    viewMode: 'panel' | 'biomarker';
    allPatientDataOverride?: any[] | null;
    patientGender?: 'male' | 'female';
    /** Optional node placed in the left sidebar of the FIRST panel section.
     *  SingleResultContent passes the provider clinical-context panel here. */
    firstPanelSidebarSlot?: React.ReactNode;
}

const ResultsView: React.FC<Props> = ({
    result,
    test_type,
    sortBy = 'date',
    filterBy = 'all',
    recommendations = [],
    onClearFilters,
    viewMode,
    allPatientDataOverride,
    patientGender,
    firstPanelSidebarSlot,
}) => {
    const currentPatient = useAppSelector(selectCurrentPatient);
    const storePatientData = useTestsByName(currentPatient?.fullName || '');
    const allPatientData = allPatientDataOverride ?? storePatientData;
    const {user} = useAuth();
    const {rules} = useDisplayRules();

    // Get gender from user info for gender-specific biomarker configs
    const gender: 'male' | 'female' | undefined =
        patientGender ??
        (user?.userInfo?.gender === 'F' ? 'female' : user?.userInfo?.gender === 'M' ? 'male' : undefined);

    // Process all individual biomarkers into a flat list
    const processedBiomarkers = useMemo(() => {
        const biomarkers: any[] = [];
        const seenTests = new Set<string>();

        // Get parent test dates as fallback for individual biomarkers
        const parentStatusDate = result.statusDate;
        const parentCreatedDate = result.createdDate;

        result.results.forEach((test: any) => {
            // Clean test name for display
            const testName = formatTestName(test.test_name);

            // Skip duplicates
            const testKey = testName;
            if (seenTests.has(testKey)) return;
            seenTests.add(testKey);

            // Skip null results for allergy tests to match panel view behavior
            if (test_type === 'allergy' && test.result === null) return;

            // Use determineStatus from biomarkerUtils for consistency (with gender for gender-specific biomarkers)
            const status = determineStatus(test, gender, rules);

            // Use individual biomarker statusDate if available, otherwise fall back to parent test dates
            const biomarkerDate = test.statusDate || parentStatusDate || parentCreatedDate;

            biomarkers.push({
                ...test,
                displayName: testName,
                status: status,
                category: test.panel_name || 'General',
                testDate: new Date(biomarkerDate),
            });
        });

        return biomarkers;
    }, [result.results, result.statusDate, result.createdDate, test_type, gender, rules]);

    // Apply filtering
    const filteredBiomarkers = useMemo(() => {
        let filtered = processedBiomarkers;

        // Apply range filter — bucket by the canonical severity tier so the
        // filter groups agree with the chip/chart/count colors everywhere.
        if (filterBy === 'normal' || filterBy === 'within-range') {
            filtered = filtered.filter((b) => severityTier(b.status) === 'normal');
        } else if (filterBy === 'abnormal') {
            filtered = filtered.filter((b) => {
                const tier = severityTier(b.status);
                return tier !== 'normal' && tier !== 'unknown';
            });
        } else if (filterBy === 'borderline' || filterBy === 'needs-attention') {
            filtered = filtered.filter((b) => severityTier(b.status) === 'borderline');
        } else if (filterBy === 'critical' || filterBy === 'high-priority') {
            filtered = filtered.filter((b) => severityTier(b.status) === 'critical');
        } else if (filterBy in HEALTH_CATEGORIES) {
            // Filter by health category using the dashboard categories
            filtered = filtered.filter((b) => {
                const category = getTestCategory(b);
                return category === filterBy;
            });
        }

        return filtered;
    }, [processedBiomarkers, filterBy]);

    // Apply sorting
    const sortedBiomarkers = useMemo(() => {
        const sorted = [...filteredBiomarkers];
        const sortKey = viewMode === 'panel' ? sortBy : sortBy === 'date' ? 'newest' : sortBy;

        switch (sortKey) {
            case 'newest':
                // Sort by date descending, then alphabetically for ties
                return sorted.sort((a, b) => {
                    const dateDiff = b.testDate.getTime() - a.testDate.getTime();
                    if (dateDiff !== 0) return dateDiff;
                    return a.displayName.localeCompare(b.displayName);
                });
            case 'oldest':
                // Sort by date ascending, then alphabetically for ties
                return sorted.sort((a, b) => {
                    const dateDiff = a.testDate.getTime() - b.testDate.getTime();
                    if (dateDiff !== 0) return dateDiff;
                    return a.displayName.localeCompare(b.displayName);
                });
            case 'name':
            case 'alphabetical':
                return sorted.sort((a, b) => a.displayName.localeCompare(b.displayName));
            case 'status':
                // Sort by severity-tier priority, then alphabetically for ties.
                // Tier order keeps the most urgent biomarkers first and always
                // agrees with the canonical color buckets.
                return sorted.sort((a, b) => {
                    const tierOrder = {critical: 0, borderline: 1, normal: 2, unknown: 3};
                    const statusDiff = tierOrder[severityTier(a.status)] - tierOrder[severityTier(b.status)];
                    if (statusDiff !== 0) return statusDiff;
                    return a.displayName.localeCompare(b.displayName);
                });
            default:
                return sorted;
        }
    }, [filteredBiomarkers, sortBy, viewMode]);

    // Group by panel for panel view
    const panelGroups = useMemo(() => {
        if (viewMode !== 'panel') return {};

        const groups: Record<string, any[]> = {};
        const processedTests = new Set<string>();

        sortedBiomarkers.forEach((biomarker: any) => {
            // Clean panel name
            let panelName = biomarker.panel_name || 'Additional Tests';
            if (panelName.includes('DTC - ')) {
                panelName = panelName.substring(6);
            }

            // Skip duplicates
            const testKey = `${panelName}-${biomarker.test_name}`;
            if (processedTests.has(testKey)) return;
            processedTests.add(testKey);

            if (!groups[panelName]) {
                groups[panelName] = [];
            }
            groups[panelName].push(biomarker);
        });

        return groups;
    }, [sortedBiomarkers, viewMode]);

    // Helper function to render biomarker cards
    const renderBiomarkerCard = (biomarker: any, index: number, panelName?: string) => {
        // Use the biomarker's own value from the result_set being viewed.
        //
        // Previously this called a `getMostRecentValue` helper that
        // walked every test in `allPatientData` and substituted the
        // latest cross-test value. That was wrong on the
        // SingleResultContent page (the only caller of this component)
        // — when a patient navigates to /result/<2023 set>, the
        // headline must show the 2023 value, not the latest. The
        // historical chart still draws the full series via
        // getHistoricalData below; only the headline number is
        // anchored to the current result_set.
        const testName = biomarker.test_name || biomarker.displayName;

        // Parse the value from the verbatim lab result. `parseNumericValue`
        // returns `null` for non-numeric results (e.g. "Test Not Performed",
        // "No Result", "TNP") rather than coercing them to 0 — that previously
        // made determineStatus see a "0" and flag the result "Low".
        const value = parseNumericValue(biomarker.result);
        const refRange = parseRefRangeWithDefault(biomarker.refRange);

        // Derive status from the REAL biomarker (not a "0" stand-in). For a
        // non-numeric result determineStatus returns 'Unknown' — keep that
        // neutral status. Previously this remapped 'Unknown' → 'Normal', which
        // falsely greenwashed non-numeric results, and a "0" stand-in made
        // genuine non-numeric results render as "Low".
        const status = determineStatus(biomarker, gender, rules);

        // Get educational content for this test
        const education = getTestEducation(testName);

        // Generate unique key using multiple fields to ensure uniqueness across panels
        // Combine panel name, test name, value, status date, and index for guaranteed uniqueness
        const baseKey = biomarker.id || testName;
        const uniqueKey = `${panelName || 'biomarker'}-${baseKey}-${value}-${
            biomarker.statusDate || 'no-date'
        }-idx${index}`;

        // BiomarkerChartCard now accepts a nullable `value`: for a non-numeric
        // result it renders the verbatim lab text in the headline, hides the
        // slider indicator (via useBiomarkerZones.hasValue), and gaps the trend
        // (the nullable trend builder drops the null point) — instead of a
        // misleading 0. The raw result string travels in the `result` prop.
        return (
            <BiomarkerChartCard
                key={uniqueKey}
                name={biomarker.displayName}
                value={value}
                units={biomarker.units || ''}
                status={status}
                rangeMin={refRange.min}
                rangeMax={refRange.max}
                history={getHistoricalData(testName, value, true, allPatientData, true)}
                lastTestDate={biomarker.statusDate}
                education={education}
                result={biomarker}
                recommendations={recommendations}
                gender={gender}
                rules={rules}
            />
        );
    };

    // Check if we have any results
    const totalResults =
        viewMode === 'panel'
            ? Object.values(panelGroups).reduce((sum, tests) => sum + tests.length, 0)
            : sortedBiomarkers.length;

    if (totalResults === 0) {
        return (
            <div className="mx-3 lg:mx-6">
                <div className="flex gap-8">
                    <div className="w-72 flex-shrink-0">
                        <h2 className="text-xl font-semibold text-gray-900 mb-1">No Results Found</h2>
                        <div className="text-sm text-gray-500">0 Biomarkers</div>
                        {filterBy !== 'all' && (
                            <div className="text-xs text-gray-400 mt-2">Filter: {getFilterDisplayName(filterBy)}</div>
                        )}
                    </div>
                    <div className="flex-1">
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
                            <div className="text-gray-400 mb-4">
                                <svg
                                    className="mx-auto h-12 w-12"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                    />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                No results for {getFilterDisplayName(filterBy)}
                            </h3>
                            <p className="text-gray-500 mb-4">
                                Try adjusting your filter settings to see more biomarkers.
                            </p>
                            {filterBy !== 'all' && onClearFilters && (
                                <button
                                    onClick={onClearFilters}
                                    className="text-sm text-mld-primary hover:text-mld-primary/80 font-medium">
                                    Clear all filters
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Panel view rendering
    if (viewMode === 'panel') {
        return (
            <div className="space-y-8">
                {Object.entries(panelGroups).map(([panelName, tests], sectionIndex) => {
                    if (tests.length === 0) return null;

                    // Get the latest test date from the panel
                    const latestDate = tests[0]?.statusDate || tests[0]?.createdDate;

                    return (
                        <div key={panelName}>
                            {/* Add separator between sections */}
                            {sectionIndex > 0 && <div className="mx-6 border-t border-gray-300 mb-8"></div>}

                            <TestPanelSection
                                title={panelName}
                                biomarkerCount={tests.length}
                                lastTestDate={latestDate}
                                sidebarSlot={sectionIndex === 0 ? firstPanelSidebarSlot : undefined}>
                                {tests.map((test: any, index: number) => renderBiomarkerCard(test, index, panelName))}
                            </TestPanelSection>
                        </div>
                    );
                })}
            </div>
        );
    }

    // Biomarker view rendering
    const latestDate =
        sortedBiomarkers.length > 0
            ? sortedBiomarkers.reduce((latest, biomarker) => {
                  const biomarkerDate = new Date(biomarker.testDate);
                  return biomarkerDate > latest ? biomarkerDate : latest;
              }, new Date(0))
            : new Date();

    return (
        <div className="mx-3 lg:mx-6">
            {/* Use same layout as TestPanelSection */}
            <div className="lg:hidden">
                {/* Mobile: Stacked layout */}
                <div className="mb-4">
                    <h2 className="text-lg font-semibold text-mld-text-primary mb-1">Individual Biomarkers</h2>
                    <div className="text-sm text-gray-500 space-y-1">
                        <div>{sortedBiomarkers.length} Biomarkers</div>
                        <div className="flex items-center gap-1">
                            <span>
                                Last Tested{' '}
                                {latestDate.toLocaleDateString('en-US', {
                                    month: 'numeric',
                                    day: 'numeric',
                                    year: '2-digit',
                                })}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                    <div className="space-y-3">
                        {sortedBiomarkers.map((biomarker, index) => renderBiomarkerCard(biomarker, index))}
                    </div>
                </div>
            </div>

            {/* Desktop: Side-by-side layout */}
            <div className="hidden lg:flex gap-8">
                {/* Left Column - Panel Info (on page background) */}
                <div className="w-72 flex-shrink-0">
                    <h2 className="text-xl font-semibold text-mld-text-primary mb-1">Individual Biomarkers</h2>
                    <div className="text-sm text-gray-500 space-y-1">
                        <div>{sortedBiomarkers.length} Biomarkers</div>
                        <div className="flex items-center gap-1">
                            <span>
                                Last Tested{' '}
                                {latestDate.toLocaleDateString('en-US', {
                                    month: 'numeric',
                                    day: 'numeric',
                                    year: '2-digit',
                                })}
                            </span>
                        </div>
                        <div className="mt-3 text-xs text-gray-400">
                            Sorted by:{' '}
                            {sortBy === 'newest'
                                ? 'Newest First'
                                : sortBy === 'oldest'
                                  ? 'Oldest First'
                                  : sortBy === 'alphabetical'
                                    ? 'A-Z'
                                    : sortBy === 'status'
                                      ? 'Status'
                                      : 'Default'}
                        </div>
                        {filterBy !== 'all' && (
                            <div className="text-xs text-gray-400">
                                Filtered by:{' '}
                                {filterBy === 'normal' || filterBy === 'within-range'
                                    ? 'Within Range'
                                    : filterBy === 'abnormal'
                                      ? 'Abnormal'
                                      : filterBy === 'borderline' || filterBy === 'needs-attention'
                                        ? 'Needs Attention'
                                        : filterBy === 'critical' || filterBy === 'high-priority'
                                          ? 'High Priority'
                                          : filterBy === 'baseline'
                                            ? 'Baseline Health'
                                            : filterBy === 'thyroid'
                                              ? 'Thyroid Health'
                                              : filterBy === 'allergy'
                                                ? 'Allergy Tests'
                                                : filterBy}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Column - Biomarker Cards in rounded panel */}
                <div className="flex-1">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                        <div className="space-y-3">
                            {sortedBiomarkers.map((biomarker, index) => renderBiomarkerCard(biomarker, index))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResultsView;
